import React from "react";
import SectionHeader from "./SectionHeader";
import Select from "../util/Select";
import Input from "../util/Input";
import MulSelectCreatable from "../util/MulSelectCreatable";
import { PropTypes } from "prop-types";

// Environment section: fields flow in a two-column grid; <Wide> takes a full row
const Section = ({ children }) => (
  <div className="AppSection regSection">
    <SectionHeader title={"Environment"} showHr={false} />
    <div className="regRow regRow--2">{children}</div>
  </div>
);

Section.propTypes = { children: PropTypes.node };

const Wide = ({ children }) => <div className="regWide">{children}</div>;

Wide.propTypes = { children: PropTypes.node };

const YES_NO_OPTIONS = [
  { key: "0", value: "Select an option" },
  { key: "1", value: "Yes" },
  { key: "2", value: "No" }
];

// Field label -> key of its validation error (see FIELD_ERROR_RULES in App.js)
const ENV_FIELD_KEYS = {
  "Certified System": "certifiedSystem",
  "NVIDIA GPUs": "gpu",
  "NVIDIA GPU": "gpu",
  "Deployment Type": "deploymentType",
  "NVIDIA GPUs Deployed": "gpuDeployed",
  "Deployment": "deployment",
  "Use Case": "useCase",
  "Estimated Number Of Users": "estimatedUsers",
  "Primary Application": "primaryApplication",
  "GPU Server Vendor": "gpuServerVendor",
  "Switch Model": "switchModel",
  "No. of Switches in Eval Envs": "evalSwitches",
  "Switch NOS": "switchNos",
  "Project Size": "projectSize",
  "Infrastructure": "infrastructure",
  "Compatible Hardware": "compatibleHardware",
  "VMware vSphere 7 U2 Enterprise Edition or later": "vmware",
  "Deployment Method": "deploymentMethod",
  "Platform": "platform",
  "Number Of Nodes": "numberOfNodes",
  "Network operating system": "networkOs",
  "Site/Premise Name": "premiseName",
  "Number Of Switches": "numberOfSwitches",
  "Total Nodes or Servers": "bcmNodes",
  "GPUs in the Environment": "bcmGpus",
  "What is your anticipated volume of API requests per day?": "apiVolume",
  "Do you plan to enroll compute infrastructure from your own cloud?": "enrollOwnCloud",
  "Are you interested in deploying NIM's": "nim",
  "Certified Server": "certifiedServer",
  "VDI Hypervisor": "vdiHypervisor",
  "VDI Seats": "vdiSeats",
  "VDI Remoting Client": "vdiRemoting"
};

const Environment = props => {
  const family = props.productFamily ? props.productFamily.toUpperCase() : "";
  const fieldErrors = props.fieldErrors || {};
  const err = label => fieldErrors[ENV_FIELD_KEYS[label]];

  if (family === "OMNIVERSE") {
    if (props.isBoxx) {
      return (
        <Section>
          <Select
            labelText={"Certified System"} errorText={err("Certified System")}
            optionValues={props.BoxxCertifiedSystemOptions}
            isRequired={true}
            onChange={props.handleCertfiiedSystemChange}
          />
          <Select
            labelText={"NVIDIA GPUs"} errorText={err("NVIDIA GPUs")}
            optionValues={props.BoxxNvidiaGPUOptions}
            isRequired={true}
            onChange={props.handleBoxxNvidiaGPUChange}
          />
          <Select
            labelText={"Deployment Type"} errorText={err("Deployment Type")}
            optionValues={props.BoxxDeploymentTypeOptions}
            isRequired={true}
            onChange={props.handleBoxDeploymentTypeChange}
          />
        </Section>
      );
    }
    return (
      <Section>
        <Select
          labelText={"NVIDIA GPUs Deployed"} errorText={err("NVIDIA GPUs Deployed")}
          optionValues={props.NvidiaGpusDeployedOptions}
          isRequired={true}
          onChange={props.handleGPUDeployed}
        />
        <Select
          labelText={"Deployment"} errorText={err("Deployment")}
          optionValues={props.deploymentOptions}
          isRequired={true}
          onChange={props.handleDeployment}
        />
        <Select
          labelText={"Use Case"} errorText={err("Use Case")}
          optionValues={props.useCaseOptions}
          isRequired={true}
          onChange={props.handleUseCaseOptions}
        />
        <Select
          labelText={"Estimated Number Of Users"} errorText={err("Estimated Number Of Users")}
          optionValues={props.estimatedUserOptions}
          isRequired={true}
          onChange={props.handleEstimatedUsersOptions}
        />
        <Wide>
          <MulSelectCreatable
            labelText={"Primary Application"} errorText={err("Primary Application")}
            optionValues={props.primaryAppOptions}
            isRequired={true}
            onChange={props.handlePrimaryAppChange}
          />
        </Wide>
        <Wide>
          <Input
            labelText={"Other Applications"} errorText={err("Other Applications")}
            placeholder={"Enter other applications"}
            isRequired={false}
            value={props.otherApplicationsVal}
            onChange={props.handleOtherValChange}
            maximumLength={160}
          />
        </Wide>
      </Section>
    );
  } else if (family === "SPECTRUM-X") {
    return (
      <Section>
        <Select
          labelText={"GPU Server Vendor"} errorText={err("GPU Server Vendor")}
          optionValues={props.stmxVendorOptions}
          isRequired={true}
          onChange={props.handleSTMXVendorChange}
        />
        <Select
          labelText={"NVIDIA GPU"} errorText={err("NVIDIA GPU")}
          optionValues={props.stmxGPUOptions}
          isRequired={true}
          onChange={props.handleNSTMXGPUChange}
        />
        <Select
          labelText={"Switch Model"} errorText={err("Switch Model")}
          optionValues={props.stmxModelOptions}
          isRequired={true}
          onChange={props.handleSTMXSwitchModelChange}
        />
        <Select
          labelText={"No. of Switches in Eval Envs"} errorText={err("No. of Switches in Eval Envs")}
          optionValues={props.stmxNosOptions}
          isRequired={true}
          onChange={props.handleSTMXSwitchNOSChange}
        />
        <Select
          labelText={"Switch NOS"} errorText={err("Switch NOS")}
          optionValues={props.stmxSwitchOptionsUOptions}
          isRequired={true}
          onChange={props.handleSTMXNosSwitchEvalChange}
        />
        <Input
          labelText={"NVIDIA Contact"} errorText={err("NVIDIA Contact")}
          placeholder={"Enter NVIDIA contact"}
          isRequired={false}
          value={props.stmxNvidiaContactVal}
          onChange={props.handleSTMXNvidiaContactChange}
          maximumLength={160}
        />
        <Wide>
          <Input
            labelText={"AI Applications"} errorText={err("AI Applications")}
            placeholder={"Enter AI applications"}
            isRequired={false}
            value={props.stmxAIAppVal}
            onChange={props.handleSTMXAIAppChange}
            maximumLength={160}
          />
        </Wide>
      </Section>
    );
  } else if (family === "NVAIE_DGX_SPARK") {
    if (props.isSnowflake) {
      return (
        <Section>
          <Select
            labelText={"Project Size"} errorText={err("Project Size")}
            optionValues={props.nvaiSizeOptions}
            isRequired={true}
            onChange={props.handleNVAIProjectSizeChange}
          />
        </Section>
      );
    }
    return (
      <Section>
        <Select
          labelText={"Use Case"} errorText={err("Use Case")}
          optionValues={props.nvaiWorkloadsOptions}
          isRequired={true}
          onChange={props.handleNVAIWorkLoadsChange}
        />
        <Select
          labelText={"Project Size"} errorText={err("Project Size")}
          optionValues={props.nvaiSizeOptions}
          isRequired={true}
          onChange={props.handleNVAIProjectSizeChange}
        />
        <Select
          labelText={"Deployment"} errorText={err("Deployment")}
          optionValues={props.nvaiVirtualOptions}
          isRequired={true}
          onChange={props.handleNVAIDeploymentChange}
        />
        <Select
          labelText={"Infrastructure"} errorText={err("Infrastructure")}
          optionValues={props.nvaiInfraOptions}
          isRequired={true}
          onChange={props.handleNVAIInfraChange}
        />
      </Section>
    );
  } else if (family === "NVAIENTERPRISE") {
    if (props.isSnowflake) {
      return (
        <Section>
          <Select
            labelText={"Use Case"} errorText={err("Use Case")}
            optionValues={props.nvaiWorkloadsOptions}
            isRequired={true}
            onChange={props.handleNVAIWorkLoadsChange}
          />
          <Select
            labelText={"Project Size"} errorText={err("Project Size")}
            optionValues={props.nvaiSizeOptions}
            isRequired={true}
            onChange={props.handleNVAIProjectSizeChange}
          />
        </Section>
      );
    }
    return (
      <Section>
        <Select
          labelText={"NVIDIA GPUs"} errorText={err("NVIDIA GPUs")}
          optionValues={props.nvaiGPUOptions}
          isRequired={true}
          onChange={props.handleNVAIGPUChange}
        />
        <Select
          labelText={"Infrastructure"} errorText={err("Infrastructure")}
          optionValues={props.nvaiInfraOptions}
          isRequired={true}
          onChange={props.handleNVAIInfraChange}
        />
        <Select
          labelText={"Use Case"} errorText={err("Use Case")}
          optionValues={props.nvaiWorkloadsOptions}
          isRequired={true}
          onChange={props.handleNVAIWorkLoadsChange}
        />
        <Select
          labelText={"Deployment"} errorText={err("Deployment")}
          optionValues={props.nvaiVirtualOptions}
          isRequired={true}
          onChange={props.handleNVAIDeploymentChange}
        />
        <Select
          labelText={"Project Size"} errorText={err("Project Size")}
          optionValues={props.nvaiSizeOptions}
          isRequired={true}
          onChange={props.handleNVAIProjectSizeChange}
        />
      </Section>
    );
  } else if (family === "NVAIE") {
    return (
      <Section>
        <Select
          labelText={"Compatible Hardware"} errorText={err("Compatible Hardware")}
          optionValues={props.CompatibleHardwareOptions}
          isRequired={true}
          onChange={props.handleCompatibleHardwareChange}
        />
        <Select
          labelText={"Estimated Number Of Users"} errorText={err("Estimated Number Of Users")}
          optionValues={props.estimatedUserOptions}
          isRequired={true}
          onChange={props.handleEstimatedUsersOptions}
        />
        <Wide>
          <Select
            labelText={"VMware vSphere 7 U2 Enterprise Edition or later"} errorText={err("VMware vSphere 7 U2 Enterprise Edition or later")}
            optionValues={props.VMWareOptions}
            isRequired={true}
            onChange={props.handleVMWareChange}
          />
        </Wide>
      </Section>
    );
  } else if (family === "UFM") {
    return (
      <Section>
        <Select
          labelText={"Deployment Method"} errorText={err("Deployment Method")}
          optionValues={props.ufmDeploymentMethodOptions}
          isRequired={true}
          onChange={props.handleUFMDeploymentMethodChange}
        />
        <Select
          labelText={"Platform"} errorText={err("Platform")}
          optionValues={props.ufmPlatformOptions}
          isRequired={true}
          onChange={props.handleUFMPlatformChange}
        />
        <Select
          labelText={"Number Of Nodes"} errorText={err("Number Of Nodes")}
          optionValues={props.ufmNumberOfNodesOptions}
          isRequired={true}
          onChange={props.handleUFMNumberOfNodesChange}
        />
      </Section>
    );
  } else if (family === "NETQ") {
    // The NetQ SaaS checkbox and SaaS portal-user fields are not part of this form
    return (
      <Section>
        <Select
          labelText={"Network operating system"} errorText={err("Network operating system")}
          optionValues={props.NETQOSOptions}
          isRequired={true}
          onChange={props.handleNetworkOperatingSystemChange}
        />
        <Input
          datatip="Enter monitoring tools currently in use"
          labelText={"Other network monitoring applications"} errorText={err("Other network monitoring applications")}
          placeholder={"Enter monitoring applications"}
          isRequired={false}
          value={props.otherNetworkElements}
          onChange={props.handleOtherNetworkElementsChange}
          maximumLength={160}
        />
      </Section>
    );
  } else if (family === "NETQSAAS") {
    return (
      <Section>
        <Select
          labelText={"Network operating system"} errorText={err("Network operating system")}
          optionValues={props.NETQOSOptions}
          isRequired={true}
          onChange={props.handleNetworkOperatingSystemChange}
        />
        <Input
          datatip="Enter monitoring tools currently in use"
          labelText={"Other network monitoring applications"} errorText={err("Other network monitoring applications")}
          placeholder={"Enter monitoring applications"}
          isRequired={false}
          value={props.otherNetworkElements}
          onChange={props.handleOtherNetworkElementsChange}
          maximumLength={160}
        />
        <Input
          datatip="Site/premise name used to logically group switches as an administrative domain"
          labelText={"Site/Premise Name"} errorText={err("Site/Premise Name")}
          placeholder={"Enter site/premise name"}
          isRequired={true}
          value={props.premiseName}
          onChange={props.handlePremiseNameChange}
          maximumLength={160}
        />
        <Input
          labelText={"Number Of Switches"} errorText={err("Number Of Switches")}
          placeholder={"Enter number of switches"}
          isRequired={true}
          value={props.numberOfSwicthes}
          onChange={props.handleNumberOfSwicthesChange}
          maximumLength={160}
        />
      </Section>
    );
  } else if (family === "PARTNEROMNIVERSE") {
    return (
      <Section>
        <Select
          labelText={"Certified System"} errorText={err("Certified System")}
          optionValues={props.PartnerCertifiedOptions}
          isRequired={true}
          onChange={props.handlePartnerCertifiedSystemChange}
        />
        <Select
          labelText={"Deployment"} errorText={err("Deployment")}
          optionValues={props.PartnerDeploymentOptions}
          isRequired={true}
          onChange={props.handlePartnerDeployment}
        />
        <Select
          labelText={"NVIDIA GPUs"} errorText={err("NVIDIA GPUs")}
          optionValues={props.PartnerNvidiaGPUOptions}
          isRequired={true}
          onChange={props.handlePartnerNvidiaGPUChange}
        />
      </Section>
    );
  } else if (family === "BCM") {
    return (
      <Section>
        <Input
          labelText={"Total Nodes or Servers"} errorText={err("Total Nodes or Servers")}
          placeholder={"Enter total nodes or servers"}
          isRequired={true}
          value={props.bcmTotalNodesVal}
          onChange={props.handleBCMTotalNodesChange}
          maximumLength={4}
        />
        <Input
          labelText={"GPUs in the Environment"} errorText={err("GPUs in the Environment")}
          placeholder={"Enter number of GPUs"}
          isRequired={true}
          value={props.BCMGPUVal}
          onChange={props.handleBCMGPUChange}
          maximumLength={4}
        />
      </Section>
    );
  } else if (family === "DGXCSLI") {
    return (
      <Section>
        <Wide>
          <Input
            labelText={"What is your anticipated volume of API requests per day?"} errorText={err("What is your anticipated volume of API requests per day?")}
            placeholder={"Enter anticipated volume"}
            isRequired={true}
            value={props.bcmTotalNodesVal}
            onChange={props.handleBCMTotalNodesChange}
            maximumLength={10}
          />
        </Wide>
        <Wide>
          <Select
            labelText={"Do you plan to enroll compute infrastructure from your own cloud?"} errorText={err("Do you plan to enroll compute infrastructure from your own cloud?")}
            optionValues={YES_NO_OPTIONS}
            isRequired={true}
            onChange={props.handlePartnerDeployment}
          />
        </Wide>
        <Wide>
          <Select
            labelText={"Are you interested in deploying NIM's"} errorText={err("Are you interested in deploying NIM's")}
            optionValues={YES_NO_OPTIONS}
            isRequired={true}
            onChange={props.handleServerChange}
          />
        </Wide>
        <Wide>
          <MulSelectCreatable
            labelText={"Primary Application"} errorText={err("Primary Application")}
            optionValues={props.primaryAppOptions}
            isRequired={true}
            onChange={props.handlePrimaryAppChange}
          />
        </Wide>
      </Section>
    );
  }

  // default: vGPU / GRID
  return (
    <Section>
      <Select
        labelText={"Certified Server"} errorText={err("Certified Server")}
        optionValues={props.serverOptions}
        isRequired={true}
        onChange={props.handleServerChange}
      />
      <Select
        labelText={"VDI Hypervisor"} errorText={err("VDI Hypervisor")}
        optionValues={props.hypervisorOptions}
        isRequired={true}
        onChange={props.handleHypervisorChange}
      />
      <Select
        labelText={"VDI Seats"} errorText={err("VDI Seats")}
        optionValues={props.seatOptions}
        isRequired={true}
        onChange={props.handleSeatsChange}
      />
      <Select
        labelText={"NVIDIA GPUs"} errorText={err("NVIDIA GPUs")}
        optionValues={props.gpuOptions}
        isRequired={true}
        onChange={props.handleGpuChange}
      />
      <Select
        labelText={"VDI Remoting Client"} errorText={err("VDI Remoting Client")}
        optionValues={props.remotingOptions}
        isRequired={true}
        onChange={props.handleRemotingChange}
      />
      <Input
        labelText={"Other Applications"} errorText={err("Other Applications")}
        placeholder={"Enter other applications"}
        isRequired={false}
        value={props.otherApplicationsVal}
        onChange={props.handleOtherValChange}
        maximumLength={160}
      />
      <Wide>
        <MulSelectCreatable
          labelText={"Primary Application"} errorText={err("Primary Application")}
          optionValues={props.primaryAppOptions}
          isRequired={true}
          onChange={props.handlePrimaryAppChange}
        />
      </Wide>
    </Section>
  );
};

// Props vary by product family, so none are marked required here
Environment.propTypes = {
  productFamily: PropTypes.string,
  fieldErrors: PropTypes.object,
  isBoxx: PropTypes.bool,
  isSnowflake: PropTypes.bool,
  isNetqSaas: PropTypes.bool,
  serverOptions: PropTypes.array,
  hypervisorOptions: PropTypes.array,
  seatOptions: PropTypes.array,
  gpuOptions: PropTypes.array,
  remotingOptions: PropTypes.array,
  primaryAppOptions: PropTypes.array,
  handleServerChange: PropTypes.func,
  handleOtherValChange: PropTypes.func,
  handleHypervisorChange: PropTypes.func,
  handleSeatsChange: PropTypes.func,
  handleGpuChange: PropTypes.func,
  handleRemotingChange: PropTypes.func,
  handlePrimaryAppChange: PropTypes.func,
  handleBoxxNvidiaGPUChange: PropTypes.func,
  handleBoxDeploymentTypeChange: PropTypes.func,
  handleCertfiiedSystemChange: PropTypes.func,
  BoxxDeploymentTypeOptions: PropTypes.array,
  BoxxCertifiedSystemOptions: PropTypes.array,
  BoxxNvidiaGPUOptions: PropTypes.array,
  handlePremiseNameChange: PropTypes.func,
  handleNumberOfSwicthesChange: PropTypes.func,
  handleNetworkOperatingSystemChange: PropTypes.func,
  handleOtherNetworkElementsChange: PropTypes.func
};

export default Environment;
